import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { CentinelaAvatar } from "../../../components/base/CentinelaChat";

/**
 * Centinela pensando: el avatar al centro y una frase a la vez.
 *
 * ── Por qué no basta un «Cargando…» ─────────────────────────────────────────
 * Armar el parte tarda: siete lecturas en paralelo, el recorrido del día punto
 * por punto, y al final Centinela escribiendo el resumen, que es lo que más se
 * lleva. Con una sola línea quieta, esos segundos se leen como que la pantalla
 * se trabó. Diciendo en qué va, la misma espera se lee como trabajo.
 *
 * ── Las fases son reales, los tiempos son estimados ─────────────────────────
 * Esto importa y por eso está escrito donde se lee. Cada frase corresponde a
 * algo que de verdad ocurre en el servidor, en ese orden. Lo que NO es real es
 * el instante en que cada una termina: el endpoint contesta una sola vez, al
 * final, así que el avance va por reloj y no por telemetría.
 *
 * Por eso los tiempos NO son parejos, y ahí está la diferencia entre informar y
 * decorar: las fases de lectura y aritmética son rápidas, y la de redacción es
 * una llamada a un modelo que se lleva la mayor parte. Repartir el reloj en
 * partes iguales dejaría la frase plantada en la última la mitad del tiempo,
 * que es justo la sensación que se quiere evitar.
 *
 * La última frase se queda hasta que llegan los datos. No se «completa» nunca:
 * darla por terminada y seguir esperando sería mentir en la única parte que
 * quien mira puede comprobar.
 *
 * ── Y si alguien pidió menos movimiento ─────────────────────────────────────
 * Con `prefers-reduced-motion` no hay desvanecido ni pulso: las frases cambian
 * en seco. El contenido es el mismo; lo que se apaga es la animación.
 */

export interface Frase {
  k: string;
  texto: string;
  /** Cuánto dura antes de dar paso a la siguiente. La última lleva 0: se queda. */
  ms: number;
}

/** Las fases de `GET /units/:id/conduccion`, en el orden en que ocurren. */
export const FRASES_PARTE: Frase[] = [
  { k: "recorrido", texto: "Midiendo el recorrido del día…", ms: 1100 },
  { k: "paradas", texto: "Analizando las paradas…", ms: 1100 },
  { k: "incidencias", texto: "Revisando las incidencias…", ms: 1000 },
  { k: "descanso", texto: "Verificando los descansos…", ms: 1000 },
  { k: "habitos", texto: "Buscando hábitos de conducción…", ms: 1200 },
  { k: "redactando", texto: "Escribiendo el parte…", ms: 0 },
];

/** Las de la evidencia: buscar en el bucket y firmar cada archivo. */
export const FRASES_EVIDENCIA: Frase[] = [
  { k: "buscando", texto: "Buscando lo que grabó la cámara…", ms: 1100 },
  { k: "descartando", texto: "Descartando capturas sin imagen…", ms: 1000 },
  { k: "firmando", texto: "Preparando los enlaces de video…", ms: 0 },
];

const SUAVE = 260; // lo que dura el desvanecido, en ms

export interface CentinelaPensandoProps {
  frases?: Frase[];
  className?: string;
}

export default function CentinelaPensando({
  frases = FRASES_PARTE,
  className = "",
}: CentinelaPensandoProps) {
  const { t } = useTranslation("common");
  const [actual, setActual] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (actual >= frases.length - 1) return;

    const espera = frases[actual].ms;
    // Se apaga un poco antes de cambiar, para que el texto no salte: primero
    // se va el anterior, después entra el siguiente.
    const apagar = setTimeout(() => setVisible(false), Math.max(espera - SUAVE, 0));
    const pasar = setTimeout(() => {
      setActual((n) => n + 1);
      setVisible(true);
    }, espera);

    return () => {
      clearTimeout(apagar);
      clearTimeout(pasar);
    };
  }, [actual, frases]);

  const frase = frases[Math.min(actual, frases.length - 1)];

  return (
    <div
      /*
        ── Por qué `h-full` y un mínimo ──────────────────────────────────────
        `justify-center` sólo centra dentro del alto que la caja tenga, y con
        `py-10` ese alto era el del contenido: el avatar quedaba pegado arriba
        del panel, con media pantalla vacía debajo. Parecía que algo se había
        colapsado, no que estuviera trabajando.

        `h-full` lo hace ocupar el cuerpo de la pestaña —que sí tiene alto
        definido— y ahí `justify-center` ya significa algo. El mínimo es para
        cuando el contenedor NO tiene alto propio: sin él volvería a encogerse
        al tamaño del contenido y estaríamos igual.
      */
      className={`flex h-full min-h-[16rem] flex-col items-center justify-center gap-3 rounded-xl bg-surface px-4 py-10 ${className}`}
    >
      <CentinelaAvatar size="xl" pulse />

      {/* Alto fijo: sin él, una frase corta y una larga cambian la altura de la
          caja y todo lo de abajo da un brinco en cada paso. */}
      <div className="flex h-5 items-center">
        <p
          className={`text-center text-xs text-content-secondary transition-opacity duration-300 motion-reduce:transition-none ${
            visible ? "opacity-100" : "opacity-0"
          }`}
        >
          {t(`monitoreoIa.conduccion.cargando.${frase.k}`, { defaultValue: frase.texto })}
        </p>
      </div>

      {/* Cuántas fases faltan, sin número ni porcentaje: un porcentaje sería
          inventado, y estos puntos sí dicen algo cierto —en cuál va. */}
      <div className="flex items-center gap-1.5">
        {frases.map((f, i) => (
          <span
            key={f.k}
            className={`h-1 rounded-full transition-all duration-300 motion-reduce:transition-none ${
              i === actual
                ? "w-4 bg-info"
                : i < actual
                  ? "w-1 bg-info/40"
                  : "w-1 bg-outline/30"
            }`}
          />
        ))}
      </div>

      {/* Un solo anuncio para el lector de pantalla, no uno por frase: leerlas
          todas en voz alta mientras avanzan solas es ruido sobre la página. */}
      <span className="sr-only" role="status">
        {t("monitoreoIa.conduccion.cargandoAria", {
          defaultValue: "Centinela está analizando el día. Puede tardar unos segundos.",
        })}
      </span>
    </div>
  );
}
